export type VisionRoomType = 'pokoj' | 'lazienka' | 'kuchnia' | 'korytarz' | 'inne';
export type VisionSurfaceCondition = 'dobry' | 'sredni' | 'trudny' | 'niepewny';
export type VisionSuggestedScope = 'malowanie' | 'gruntowanie' | 'gladz' | 'szpachlowanie' | 'inne';

export interface VisionEstimateResult {
  roomType: VisionRoomType;
  detectedElements: {
    doors: number;
    windows: number;
    visibleWalls: number;
    sockets?: number;
  };
  surfaceCondition: VisionSurfaceCondition;
  suggestedScope: VisionSuggestedScope[];
  observations: string[];
  warnings: string[];
  confidence: number;
  requiresManualConfirmation: string[];
}

export const defaultVisionEstimateResult: VisionEstimateResult = {
  roomType: 'inne',
  detectedElements: {
    doors: 0,
    windows: 0,
    visibleWalls: 0,
  },
  surfaceCondition: 'niepewny',
  suggestedScope: ['inne'],
  observations: [],
  warnings: ['Analiza zdjęcia jest chwilowo niedostępna. Możesz dalej policzyć wycenę ręcznie. Zdjęcie służy tylko jako kontekst, nie jako pomiar.'],
  confidence: 0,
  requiresManualConfirmation: [
    'powierzchnia podłogi',
    'łączny metraż ścian',
    'czy w zakres wchodzi sufit',
    'liczba okien i drzwi',
  ],
};

const ROOM_TYPES: VisionRoomType[] = ['pokoj', 'lazienka', 'kuchnia', 'korytarz', 'inne'];
const SURFACE_CONDITIONS: VisionSurfaceCondition[] = ['dobry', 'sredni', 'trudny', 'niepewny'];
const SCOPE_VALUES: VisionSuggestedScope[] = ['malowanie', 'gruntowanie', 'gladz', 'szpachlowanie', 'inne'];

const KNOWN_TRANSLATIONS: Array<[RegExp, string]> = [
  [/walls appear to be painted/gi, 'Ściany wyglądają na pomalowane.'],
  [/painted and in good condition/gi, 'Ściany wyglądają na pomalowane i w dobrym stanie.'],
  [/one door is visible/gi, 'Widoczne są jedne drzwi.'],
  [/door is visible/gi, 'Widoczne są drzwi.'],
  [/no windows are directly visible/gi, 'Nie widać okien bezpośrednio w kadrze.'],
  [/no windows/gi, 'Nie widać okien w kadrze.'],
  [/surface condition is uncertain/gi, 'Stan podłoża wymaga potwierdzenia.'],
  [/manual confirmation required/gi, 'Wymagane potwierdzenie wykonawcy.'],
];

export function normalizeVisionEstimateResult(input: unknown): VisionEstimateResult {
  try {
    const data = input && typeof input === 'object' ? input as Record<string, unknown> : {};
    const detectedElements = data.detectedElements && typeof data.detectedElements === 'object'
      ? data.detectedElements as Record<string, unknown>
      : {};

    const suggestedScope = normalizeStringArray(data.suggestedScope)
      .map(normalizeScope)
      .filter((scope): scope is VisionSuggestedScope => Boolean(scope));

    return {
      roomType: normalizeRoomType(data.roomType),
      detectedElements: {
        doors: safeInteger(detectedElements.doors),
        windows: safeInteger(detectedElements.windows),
        visibleWalls: safeInteger(detectedElements.visibleWalls),
        ...(detectedElements.sockets === undefined ? {} : { sockets: safeInteger(detectedElements.sockets) }),
      },
      surfaceCondition: normalizeSurfaceCondition(data.surfaceCondition),
      suggestedScope: suggestedScope.length > 0 ? [...new Set(suggestedScope)] : ['inne'],
      observations: normalizePolishTexts(data.observations),
      warnings: normalizePolishTexts(data.warnings),
      confidence: clampNumber(data.confidence, 0, 1),
      requiresManualConfirmation: normalizePolishTexts(data.requiresManualConfirmation),
    };
  } catch {
    return defaultVisionEstimateResult;
  }
}

function safeInteger(value: unknown): number {
  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numeric) ? Math.max(0, Math.trunc(numeric)) : 0;
}

function clampNumber(value: unknown, min: number, max: number): number {
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) return min;
  return Math.min(max, Math.max(min, numeric));
}

function normalizeStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => typeof item === 'string' ? item.trim() : '')
    .filter(Boolean)
    .slice(0, 8);
}

function normalizeRoomType(value: unknown): VisionRoomType {
  const normalized = normalizeAscii(value);
  if (normalized === 'room' || normalized === 'pokoj' || normalized === 'pokój') return 'pokoj';
  if (normalized === 'bathroom' || normalized === 'lazienka') return 'lazienka';
  if (normalized === 'kitchen' || normalized === 'kuchnia') return 'kuchnia';
  if (normalized === 'hall' || normalized === 'corridor' || normalized === 'korytarz') return 'korytarz';
  return ROOM_TYPES.includes(normalized as VisionRoomType) ? normalized as VisionRoomType : 'inne';
}

function normalizeSurfaceCondition(value: unknown): VisionSurfaceCondition {
  const normalized = normalizeAscii(value);
  if (['good', 'fresh', 'smooth', 'ok', 'dobry'].includes(normalized)) return 'dobry';
  if (['medium', 'average', 'sredni', 'średni'].includes(normalized)) return 'sredni';
  if (['bad', 'hard', 'damaged', 'trudny'].includes(normalized)) return 'trudny';
  return SURFACE_CONDITIONS.includes(normalized as VisionSurfaceCondition)
    ? normalized as VisionSurfaceCondition
    : 'niepewny';
}

function normalizeScope(value: string): VisionSuggestedScope | null {
  const normalized = normalizeAscii(value);
  if (['painting', 'paint', 'malowanie'].includes(normalized)) return 'malowanie';
  if (['primer', 'priming', 'grunt', 'gruntowanie'].includes(normalized)) return 'gruntowanie';
  if (['plaster', 'skim coat', 'gladz', 'gładź'].includes(normalized)) return 'gladz';
  if (['putty', 'patching', 'szpachlowanie'].includes(normalized)) return 'szpachlowanie';
  if (SCOPE_VALUES.includes(normalized as VisionSuggestedScope)) return normalized as VisionSuggestedScope;
  return value ? 'inne' : null;
}

function normalizePolishTexts(value: unknown): string[] {
  return normalizeStringArray(value).map((text) => {
    let next = text;
    for (const [pattern, replacement] of KNOWN_TRANSLATIONS) {
      next = next.replace(pattern, replacement);
    }
    return next;
  });
}

function normalizeAscii(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');
}
